import {HttpClient} from '@angular/common/http';
import {Injectable} from "@angular/core";
import {Router} from "@angular/router";
import {Angulartics2} from 'angulartics2';
import {BehaviorSubject, Observable, from, of, Subject, throwError} from 'rxjs';
import {forkJoin} from "rxjs/observable/forkJoin";
import {map, mergeMap} from 'rxjs/operators';
import {environment} from "../../../environments/environment";
import {Contact} from "../../shared/_models/contact";
import {ApplyPaymentOptions} from "../../shared/_models/payment";
import {PaymentMethod} from "../../shared/_models/paymentmethod";
import {FetchVehicleOptions, GetVehiclesResponse, TransferVehicleOptions, Vehicle} from "../../shared/_models/vehicle";
import {EXCEPTION_TYPES, MARKETS, ORGANIZATION_TYPES, USER_EVENT_TYPE} from "../constants";
import {AuthService} from "./auth.service";
import {EventTrackingService} from './event-tracking.service';
import {UtilitiesService} from "./utilities.service";
import {WrenchApiService} from './wrench-api.service';
import {ServiceResponse} from "../../shared/_models/serviceResponse";
import {LookupVehiclesParams, LookupVehiclesResponse} from "../../shared/_models/lookupVehicles";
import {LookupVehicleResponse} from "../../shared/_models/lookupVehicle";
import {AddVehicleResponse} from "../../shared/_models/addVehicle";
import { RoleService } from './role.service';
import {OrgContext, Role} from 'app/shared/_models/role';
import {YmServiceableZipParams} from "../../shared/_models/ymServiceableZipParams";
import {LsServiceableLocationParams} from 'app/shared/_models/LsServiceableLocationParams';
import {ValueLabel} from "../../shared/_models/valueLabel";
import { ConstantsService } from './constants.service';
import {User} from "../../shared/_models/user";
import {ImportEmployeesOptions} from "app/shared/_models/importEmployeesOptions";
import {FindRolesResponse} from "app/shared/_models/findRolesResponse";
import {ImportEmployeesResponse} from "app/shared/_models/importEmployeesResponse";
import { FindRolesOptions } from 'app/shared/_models/findRolesOptions';
import {GetClientTokenResponse} from "../../shared/_models/getClientTokenResponse";
import {FindJobsRequest, FindJobsResponse} from '../../shared/_models/findJobsOptions';
import {AddVehiclesParams} from "app/shared/_models/addVehiclesParams";
import {CreateAccountRequest, CreateAccountResponse} from "../../shared/_models/create-account";

declare let geolib: any;

declare global {
    interface Window {
        dataLayer: any[];
        scrollIFrameParent: boolean;
        initIFrame(iframeId: string): string;
    }
}

@Injectable()
export class UserService extends WrenchApiService {
    paymentMethodId: string;
    prospect: any;
    jobId: string;
    userServiceRating: string;
    unsupportedBrowser: any = null;
    currentLocationRequested = "";
    previousLocationRequested = "";
    loginEmail = "";

    userData = new BehaviorSubject<any>({});
    loginFromAdmin = new BehaviorSubject<boolean>(false);
    contactList = new BehaviorSubject<Contact[]>([]);
    static _marketData: Subject<any> = new BehaviorSubject<any>({});
    private _market: Subject<any> = new BehaviorSubject<any>('');

    marketData = UserService._marketData.asObservable();
    market = this._market.asObservable();

    private geoOptions = {
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: 0
    };
    selectedRoleOrgContext: OrgContext;

    constructor(
        protected http: HttpClient,
        protected authService: AuthService,
        protected router: Router,
        protected eventTrackingService: EventTrackingService,
        private utilitiesService: UtilitiesService,
        private roleService: RoleService,
        private angulartics2: Angulartics2,
        private constantService: ConstantsService) {

        super(http, authService, router, eventTrackingService);

        this.initializeMarket();

        // initialize user data from local storage
        let userData: any = {};
        const localUserData = this.utilitiesService.getLocal('userData');
        try {
            if (UtilitiesService.isNotEmpty(localUserData)) {
                userData = JSON.parse(localUserData);
            }
        } catch (e) {
            UtilitiesService.consoleLog(e);
        }
        this.updateUserData(userData);

        if (!UtilitiesService.isEmpty(userData) && userData.market) {
            this.getMarketData({market: userData.market}, "user-service").subscribe();
        }

        // initialize contact list from local storage
        let contactList = [];
        try {
            contactList = JSON.parse(this.utilitiesService.getLocal('contactList'));
        } catch (e) {
            UtilitiesService.consoleLog(e);
        }
        this.updateContactList(contactList);

        // get selected role
        this.roleService.selectedUserRole$.subscribe((role: Role) => {
            if (role) {
                this.selectedRoleOrgContext = role.orgContext;
            } else {
                this.selectedRoleOrgContext = null;
            }
        });
    } // constructor

    geolocationError(err: any) {
        // user may have denied location access
    }

    // get user's current location
    getLocation() {
        if (environment.app) {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(this.findNearestMarket, this.geolocationError, this.geoOptions);
            } else {
                // geolocation not supported by browser
            }
        }
    }

    // find nearest market to user's location
    private findNearestMarket(position: any) {

        let markets = {};
        MARKETS.forEach((m: any) => markets[m.market] = {latitude: m.latitude, longitude: m.longitude});

        if (position && position.coords && typeof geolib !== 'undefined') {
            let nearestMarket = geolib.findNearest({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude
            }, markets);
            // following code used for testing
            // let nearestMarket = geolib.findNearest({latitude: 46.055645, longitude: -118.346359}, this.markets);  // walla walla
            // let nearestMarket = geolib.findNearest({latitude: 40.730610, longitude: -73.935242}, this.markets); // new york
            // let nearestMarket = geolib.findNearest({latitude: 45.6318, longitude: -122.6716}, this.markets); // vancouver wa

            // if distance to nearest market > 100 miles (160934 meters) default to outofmarket
            let distance = geolib.getDistance({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude
            }, markets[nearestMarket.key]);

            // Even if freegeoip has set the market, override
            if (distance > 160934) {
                this.setMarket("out_of_area");
            } else {
                this.setMarket(nearestMarket.key);
            }
        }

    } // findNearestMarket()

    private initializeMarket() {

        // first check if we have anything in local storage
        let market = this.utilitiesService.getLocal("market");

        if (!this.utilitiesService.isEmpty(market)) {
            this._market.next(market);
            return;
        }

        this.getLocation();

        // not in local storage so initialize based on ip
        this.http.get('https://api.ipstack.com/check?access_key=fc5de12fb0850287ca794be59f7c8e2b').subscribe(
            (resp: any) => {

                if (resp != null && resp.hasOwnProperty("ip")) {
                    this.utilitiesService.setLocal("ip", resp.ip);

                    // make sure we don't overwrite if we set this in the meantime
                    market = this.utilitiesService.getLocal("market");
                    if (!this.utilitiesService.isEmpty(market)) {
                        return market;
                    }

                    // find nearest market based on user's IP
                    this.findNearestMarket({coords: {latitude: resp.latitude, longitude: resp.longitude}});
                }

            });

    } // initializeMarket()

    setMarket(market: any) {
        this.utilitiesService.setLocal("market", market);
        this._market.next(market);
    } // setMarket()

    clearUserData() {
        this.updateUserData({});
        this.utilitiesService.removeLocal("userData");
    } // clearUserData()

    getUserData() {
        return this.userData.asObservable();
    } // getUserData()

    getCurrentUserData() {
        return this.userData.getValue();
    } // getCurrentUserData()

    updateUserData(userData: any) {
        this.userData.next(userData);
    } // updateUserData()

    getLoginAs() {
        return this.loginFromAdmin.asObservable();
    } // getLoginAs()

    setLoginAs(loginAs: boolean) {
        this.loginFromAdmin.next(loginAs);
    } // setLoginAs()

    checkAccountExists(params: any, component: string): Observable<any> {
        return this.request(
            "/user/checkaccountexists",
            params,
            component,
            USER_EVENT_TYPE.CHECK_ACCOUNT_EXISTS,
            true);
    } // checkAccountExists()

    findUsers(params: any, component: string): Observable<any> {
        return this.request(
            "/user/findusers",
            params,
            component,
            null);
    } // findUsers()

    getUser() {
        let params = {
            userId: this.authService.getUserIdFromToken(),
            organizationId: 0,
            regionId: 0,
            hubId: 0
        };

        if (UtilitiesService.isNotEmpty(this.selectedRoleOrgContext)) {
            params.organizationId = this.selectedRoleOrgContext.organizationId;
            params.regionId = this.selectedRoleOrgContext.regionId;
            params.hubId = this.selectedRoleOrgContext.hubId;
        }

        return this.http.post(this.authService.getBackendUrl("/user/getuser"), JSON.stringify(params), this.authService.getAuthHeaders())
            .map((res: any) => {
                if (!this.utilitiesService.isEmpty(res.responseCode) && res.responseCode == "100" && !this.utilitiesService.isEmpty(res)) {
                    let userData = res.user;

                    userData.provider = this.authService.getProviderFromToken();
                    userData.advantageMember = userData.membershipCode == 1002 || userData.membershipCode == 1003;
                    this.utilitiesService.setLocal("userData", JSON.stringify(userData));

                    this.authService.userOrganizationId = userData.organizationId;

                    this.angulartics2.setUsername.next(this.authService.getUserIdFromToken().toString());
                    this.updateUserData(userData);
                    if (UtilitiesService.isNotEmpty(userData.market)) {
                        this.getMarketData({market: userData.market}, "user-service").subscribe();
                    }
                    if (this.roleService.currentRoleId === 0) {
                        this.setRole(userData);
                    }
                    return userData;

                } else {
                    // could use user.reject, but thought that was pointless in this scenario
                    return {};
                }
            })
            .catch(error => {
                return this.handleError(error);
            });
    } // getUser()

    login(loginModel: any) {
        return this.constantService.APP_CONSTANTS.pipe(
            map(c => c.hasOwnProperty("userSsoEnabled") ? c.userSsoEnabled : true),
            mergeMap(ssoEnabled => {
                if (ssoEnabled) {
                    return from(this.authService.loginWithSso(loginModel)).pipe(mergeMap((result: any) => {
                        if (result && this.utilitiesService.isNotEmpty(result.signInUserSession)) {
                            return forkJoin([this.getUser(), this.getContacts()]).map(() => {
                                return result;
                            });
                        } else {
                            if (result.name === EXCEPTION_TYPES.USER_NOT_FOUND) {
                                return this.loginWithWrench(loginModel);
                            } else {
                                return of(result);
                            }
                        }
                    }
                    )
                    ).catch((error: any) => {
                        return this.handleError(error);
                    });
                }
                return this.loginWithWrench(loginModel);
            })
        );
}

    loginWithWrench(loginModel: any) {
        return this.authService.loginWithWrench(loginModel).pipe(
            mergeMap((result: any) => {
                    if (result.responseCode == 100 && UtilitiesService.isNotEmpty(result.token)) {
                        this.utilitiesService.setLocal("loginWithToken", true);
                        return forkJoin([this.getUser(), this.getContacts()]).map(() => {
                            return result;
                        });
                    } else {
                        return of(result);
                    }
                }
            )
        ).catch(error => {
            return this.handleError(error);
        });
    } // loginWithWrench()

    completeLogin(loginModel: any) {
        this.updateUserData({});
        const loginObservable = from(this.authService.completeNewPassword(loginModel));
        return loginObservable.pipe(mergeMap((result: any) => {
                    if (result && this.utilitiesService.isNotEmpty(result.signInUserSession)) {
                        let observableArray = [];
                        observableArray.push(this.getUser());
                        observableArray.push(this.getContacts());

                        return forkJoin(observableArray).map(() => {
                            return result;
                        });

                    } else {
                        return of(result);
                    }
                }
            )
        ).catch((error: any) => {
            return this.handleError(error);
        });

    } // completeLogin()

    loginWithToken(token: any) {
        return this.authService.loginWithToken(token).pipe(
            mergeMap((result: any) => {
                    if (result) {
                        let observableArray = [];
                        observableArray.push(this.getUser());
                        observableArray.push(this.getContacts());

                        return forkJoin(observableArray).map(results => {
                            return result;
                        });
                    } else {
                        return of(result);
                    }
                }
            )
        );
    } // loginWithToken()

    logout() {
        this.updateUserData({});
        this.roleService.setUserRole(null);
        this.updateContactList([]);
        this.authService.logout();
        this.eventTrackingService.prospectId = 0;

    } // logout()

    /**
     * Updates the user
     * @param params
     * @currentUser Indicates whether the user being updated is currently signed in
     */
    updateUser(params: User, currentUser: boolean = true) {
        if (currentUser) {
            this.utilitiesService.setLocal("userData", JSON.stringify(params));
        }

        return this.request(
            '/user/updateuser',
            params,
            'user-service',
            USER_EVENT_TYPE.UPDATE_USER
        );
    }

    /**
     * changes the password for the user
     * @param params
     */
    resetPassword(params: any) {
        return this.request(
            '/user/resetlogin',
            params,
            'user-service',
            USER_EVENT_TYPE.RESET_LOGIN
        );
    } // changePassword()

    /**
     * changes the password for a provider
     * we need this because provider password reset requests come here
     * @param params
     */
    changeProviderPassword(params: any) {
        // TODO: need to figure out how to delete password from params in user event before switching to new request implementation
        return this.http.post(this.authService.getBackendUrl("/provider/resetlogin"), JSON.stringify(params), this.authService.getAuthHeaders())
            .map((resp: any) => {
                    if (resp.responseCode == 100) {
                        localStorage.setItem('token', resp.token);
                    }
                    return resp;
                }
            )
            .catch(error => {
                return this.handleError(error);
            });
    } // changeProviderPassword()

    /**
     * request a reset password email
     * @param params
     * @param component - name of the calling component
     */
    forgotPassword(params: {email: string}, component: string) {
        return this.request("/user/forgotpassword", params, component, USER_EVENT_TYPE.FORGOT_PASSWORD, true);
    } // forgotPassword()

    /**
     * creates a prospect
     * @param params - the endpoint params to pass
     * @param component - name of the calling component
     */
    newProspect(params: any, component: string) {
        params.brand = environment.brand;
        params.persistentKey = this.eventTrackingService.persistentKey;
        params.sessionKey = this.eventTrackingService.sessionKey;
        return this.request(
            "/user/newprospect",
            params,
            component,
            USER_EVENT_TYPE.CREATE_PROSPECT,
            true).pipe(
                map((response: any) => {
                    this.eventTrackingService.prospectId = response.prospectId;
                    return response;
                })
            );

    } // newProspect()

    /**
     * creates a Referral
     * @param params - the endpoint params to pass
     * @param component - name of the calling component
     */
    addReferral(params: any, component: string) {

        params.persistentKey = this.eventTrackingService.persistentKey;
        params.sessionKey = this.eventTrackingService.sessionKey;

        return this.request(
            "/user/addreferral",
            params,
            component,
            USER_EVENT_TYPE.ADD_REFERRAL,
            true
        );
    } // addReferral()

    /**
     * Get Partner Id
     * @param params - the endpoint params to pass
     * @param component - name of the calling component
     */
    getPartnerId(params: any, component: string) {
        return this.request(
            "/user/getpartnerid",
            params,
            component,
            USER_EVENT_TYPE.GET_PARTNER_ID,
            true
        );
    } // getPartnerId()

    /**
     * creates a user from a prospect
     * @param params
     */
    createUser(params: CreateAccountRequest): Observable<CreateAccountResponse> {
        params.user.defaultBrand = environment.brand;
        return this.http.post(this.authService.getBackendUrl("/user/createuser"),
            JSON.stringify(params),
            this.authService.getBasicAuthHeaders()
        ) as Observable<CreateAccountResponse>;
    } // createUser()

    /**
     * validates a location as in our service area
     * @param params - the request payload
     * @param component - the calling component name (for logging)
     */
    validateLocation(params: any, component: string) {
        return this.request(
            '/user/validatelocation',
            params,
            component,
            USER_EVENT_TYPE.VALIDATE_LOCATION,
            true
        ).pipe(
            map((response: any) => {
                if (response.responseCode == 100 && UtilitiesService.isNotEmpty(response.market)) {
                    this.utilitiesService.setLocal('market', response.market);
                }
                return response;
            })
        );
    } // validateLocation()

    /**
     * validates a location as in our service area
     * @param params
     */
    deprecatedValidateLocation(params: any) {
        return this.http.post(this.authService.getBackendUrl("/user/validatelocation"), JSON.stringify(params), this.authService.getBasicAuthHeaders())
            .catch(error => {
                return this.handleError(error);
            });
    } // deprecatedValidateLocation()

    /**
     * adds a new location to the user
     * @param params
     */
    addLocation(params: any) {
        return this.http.post(this.authService.getBackendUrl("/user/addlocation"), JSON.stringify(params), this.authService.getAuthHeaders())
            .catch(error => {
                return this.handleError(error);
            });
    } // addLocation()

    /**
     * adds a shared location to the user
     * @param params
     */
    addSharedLocation(params: any) {
        return this.http.post(this.authService.getBackendUrl("/user/addsharedlocation"), JSON.stringify(params), this.authService.getAuthHeaders())
            .catch(error => {
                return this.handleError(error);
            });
    } // addSharedLocation()

    /**
     * creates a new location
     * @param params
     */
    newLocation(params: any, component: string) {
        return this.request(
            '/user/newlocation',
            params,
            component,
            USER_EVENT_TYPE.NEW_USER_ADD_LOCATION,
            true
        );
    } // newLocation()

    /**
     * removes a location from the user
     * @param params
     */
    removeLocation(params: any, component: string = 'user-service') {
        return this.request(
            '/user/removelocation',
            params,
            component,
            USER_EVENT_TYPE.REMOVE_LOCATION,
        );
    } // removeLocation()

    /**
     * updates a location
     */
    updateLocation(params: any, component: string = 'user-service') {
        if (this.selectedRoleOrgContext) {
            params.organizationId = this.selectedRoleOrgContext.organizationId;
            params.regionId = this.selectedRoleOrgContext.regionId;
            params.hubId = this.selectedRoleOrgContext.hubId;
        }
        return this.request(
            '/user/updatelocation',
            params,
            component,
            USER_EVENT_TYPE.UPDATE_LOCATION
        );
    } // updateLocation()

    /**
     * gets all locations for a user's current role
     */
    getAllLocations(component: string = 'user-service') {
        let params: any = { userId: this.authService.getUserIdFromToken() };
        if (this.selectedRoleOrgContext) {
            params.organizationId = this.selectedRoleOrgContext.organizationId;
            params.regionId = this.selectedRoleOrgContext.regionId;
            params.hubId = this.selectedRoleOrgContext.hubId;
        }
        return this.request(
            '/user/getlocations',
            params,
            component,
            USER_EVENT_TYPE.GET_LOCATION);
    }

    /**
     * creates a job request
     * @param jobRequestData - the job request to create
     * @param component - the component for user event tracking
     */
    createJobRequest(jobRequestData: any, component?: string) {
        jobRequestData.brand = environment.brand;
        return this.request(
            '/job/addjobrequest',
            jobRequestData,
            component ? component : '',
            USER_EVENT_TYPE.CREATE_JOB_REQUEST
        );

    } // createJobRequest()

    /**
     * creates multiple job requests
     * @param jobRequestData
     */
    createJobRequests(jobRequestData: any, linkJobReqeusts: boolean, component: string) {
        jobRequestData = {'jobRequests': jobRequestData, 'linkJobRequests': linkJobReqeusts};
        // jobRequestData = {'jobRequests': jobRequestData};
        return this.request('/job/addjobrequests', jobRequestData, component, USER_EVENT_TYPE.CREATE_JOB_REQUESTS);
    } // createJobRequests()

    /**
     * gets a client token from the server
     * this is to be used with transactions
     * @param params
     */
    getClientToken(component: string, params?: any): Observable<GetClientTokenResponse> {
        return this.request(
            '/transaction/getclienttoken',
            params,
            component,
            USER_EVENT_TYPE.GET_CLIENT_TOKEN,
            true
        );
    } // getClientToken()

    /**
     * gets all payment methods for current user
     * @param component
     */
    getPaymentMethods(component: string) {
        return this.getPaymentMethodsForUser(this.authService.getUserIdFromToken(), component);
    } // getPaymentMethod()

    /**
     * gets all payment methods for specified user
     */
    getPaymentMethodsForUser(userId: number, component: string) {
        let params: any = {userId: userId};
        if (this.selectedRoleOrgContext) {
            params.organizationId = this.selectedRoleOrgContext.organizationId;
            params.regionId = this.selectedRoleOrgContext.regionId;
            params.hubId = this.selectedRoleOrgContext.hubId;
        }
        return this.request(
            '/transaction/getpaymentmethods',
            params,
            component,
            USER_EVENT_TYPE.GET_PAYMENT_METHODS
        );

    } // getPaymentMethodByUserId()

    /**
     * adds a payment method to the user
     * @param params
     * @param component
     */
    addPaymentMethod(params: PaymentMethod, component: string): Observable<any> {
        if (this.selectedRoleOrgContext) {
            params.organizationId = this.selectedRoleOrgContext.organizationId;
            params.regionId = this.selectedRoleOrgContext.regionId;
            params.hubId = this.selectedRoleOrgContext.hubId;
        }
        return this.request(
            '/transaction/addpaymentmethod',
            params,
            component,
            USER_EVENT_TYPE.CREATE_PAYMENT_METHOD
        );
    } // addPaymentMethod()

    /**
     * update a payment method
     * @param params
     * @param component
     */
    updatePaymentMethod(params: PaymentMethod, component: string) {
        if (this.selectedRoleOrgContext) {
            params.organizationId = this.selectedRoleOrgContext.organizationId;
            params.regionId = this.selectedRoleOrgContext.regionId;
            params.hubId = this.selectedRoleOrgContext.hubId;
        }
        return this.request(
            '/transaction/updatepaymentmethod',
            params,
            component,
            USER_EVENT_TYPE.UPDATE_PAYMENT_METHOD
        );

    } // updatePaymentMethod()

    /**
     * removes a payment method for the user
     * @param paymentMethodId
     * @param component
     */
    removePaymentMethod(paymentMethodId: number, component: string) {
        return this.request(
            '/transaction/removepaymentmethod',
            {paymentMethodId: paymentMethodId},
            component,
            USER_EVENT_TYPE.REMOVE_PAYMENT_METHOD
        );

    } // removePaymentMethod()

    /**
     * verifies a payment method for the user
     * @param params
     * @param component
     */
    verifyPaymentMethod(params: PaymentMethod, component: string) {
        return this.request(
            '/transaction/verifypaymentmethod',
            params,
            component,
            USER_EVENT_TYPE.VERIFY_PAYMENT_METHOD
        );

    } // verifyPaymentMethod()

    viewTransactions(params: {jobId: number, userId: number}, component: string) {
        return this.request(
            '/transaction/viewtransactions',
            params,
            component,
            USER_EVENT_TYPE.VIEW_TRANSACTIONS
        );
    }

    /**
     * gets all the transactions
     */
    getAllTransactions() {
        let params = {userId: this.authService.getUserIdFromToken()};
        return this.http.post(this.authService.getBackendUrl("/transaction/viewtransactions"), JSON.stringify(params), this.authService.getAuthHeaders())
            .catch(error => {
                return this.handleError(error);
            });
    } // getAllTransactions()

    /**
     * gets all credits available to user
     */
    getCredits() {
        let params = {userId: this.authService.getUserIdFromToken()};
        return this.http.post(this.authService.getBackendUrl("/transaction/getusercredits"), JSON.stringify(params), this.authService.getAuthHeaders())
            .catch(error => {
                return this.handleError(error);
            });
    } // getCredits()

    /**
     * un-authed call to save a promo code
     * @param params
     * @param component
     */
    savePromoCode(params: any, component?: string) {
        return this.request(
            '/transaction/savepromocode',
            params,
            component,
            USER_EVENT_TYPE.SAVE_PROMO_CODE,
            true
        ).pipe(
            map((response) => {
                // log special error codes
                if (response.responseCode == 202) {
                    this.eventTrackingService.trackErrorEvent(component,
                        'error - invalid discount code',
                        USER_EVENT_TYPE.SAVE_PROMO_CODE,
                        '/transaction/savepromocode',
                        params,
                        response.responseCode);
                } else if (response.responseCode == 502) {
                    this.eventTrackingService.trackErrorEvent(component,
                        'error - ineligible for discount',
                        USER_EVENT_TYPE.SAVE_PROMO_CODE,
                        '/transaction/savepromocode',
                        params,
                        response.responseCode);
                }
                return response;
            })
        );
    } // savePromoCode()

    /**
     * authed call to save a promo code
     * @param params
     * @param component
     */
    applyPromoCode(params: any, component?: string) {
        return this.request(
            '/transaction/applypromocode',
            params,
            component,
            USER_EVENT_TYPE.SAVE_PROMO_CODE
        ).pipe(
            map((response) => {
                // log special error codes
                if (response.responseCode == 202) {
                    this.eventTrackingService.trackErrorEvent(component,
                        'error - invalid discount code',
                        USER_EVENT_TYPE.SAVE_PROMO_CODE,
                        '/transaction/applypromocode',
                        params,
                        response.responseCode);
                } else if (response.responseCode == 502) {
                    this.eventTrackingService.trackErrorEvent(component,
                        'error - ineligible for discount',
                        USER_EVENT_TYPE.SAVE_PROMO_CODE,
                        '/transaction/applypromocode',
                        params,
                        response.responseCode);
                }
                return response;
            })
        );
    } // applyPromoCode()

    getInvoices(params: any, component: string) {
        if (this.selectedRoleOrgContext) {
            params.organizationId = this.selectedRoleOrgContext.organizationId;
            params.regionId = this.selectedRoleOrgContext.regionId;
            params.hubId = this.selectedRoleOrgContext.hubId;
        }
        return this.request(
            '/transaction/getinvoices',
            params,
            component,
            USER_EVENT_TYPE.FLEET_GET_INVOICES
        );
    } // getInvoices()

    getInvoice(params: any) {
        return this.http.post(this.authService.getBackendUrl("/transaction/getinvoice"), JSON.stringify(params), {
            headers: this.authService.getAuthHeaders().headers,
            responseType: 'blob',
            observe: 'response'
        })
            .map(UtilitiesService.downloadPdf)
            .catch(error => {
                return this.handleError(error);
            });
    } // getInvoices()

    getInvoiceData(params: any, component: string) {
        return this.request(
            '/transaction/getinvoice',
            params,
            component,
            USER_EVENT_TYPE.FLEET_GET_INVOICES
        );
    } // getInvoices()

    /**
     * gets all jobs for a user
     */
    findJobs(params: FindJobsRequest = {}, component: string = 'user-service'): Observable<FindJobsResponse> {
        if (this.selectedRoleOrgContext) {
            params.organizationId = this.selectedRoleOrgContext.organizationId;
            params.regionId = this.selectedRoleOrgContext.regionId;
            params.hubId = this.selectedRoleOrgContext.hubId;
        }
        return this.request(
            '/job/findjobs',
            params,
            component,
            USER_EVENT_TYPE.GET_ALL_JOBS
        );
    } // getAllJobs()

    /**
     * gets job requests for a user
     */
    findJobRequests(params: any = {}, component: string = 'user-service') {
        if (this.selectedRoleOrgContext) {
            params.organizationId = this.selectedRoleOrgContext.organizationId;
            params.regionId = this.selectedRoleOrgContext.regionId;
            params.hubId = this.selectedRoleOrgContext.hubId;
        } else if (!params.userId || params.userId === 0) {
            params.userId = this.authService.getUserIdFromToken();
        }
        return this.request(
            '/job/findjobrequests',
            params,
            component,
            USER_EVENT_TYPE.FIND_JOB_REQUESTS
        );
    } // getAllJobs()

    /**
     * gets job notes for a given job id
     */
    getJobNotes(params: any) {
        return this.http.post(this.authService.getBackendUrl("/job/getjobnotes"), JSON.stringify(params), this.authService.getAuthHeaders())
            .catch(error => {
                return this.handleError(error);
            });
    } // getJobNotes()

    /**
     * looks up a vehicle from a license plate or vin
     * @param params
     * @param component
     */
    lookupVehicle(params: Vehicle, component: string): Observable<LookupVehicleResponse> {
        return this.request(
            '/user/lookupvehicle',
            params,
            component,
            USER_EVENT_TYPE.LOOKUP_VEHICLE,
            true
        );
    } // lookupVehicle()

    /**
     * get the vehicles for the user
     */
    getVehicles(component: string, params: any = null): Observable<GetVehiclesResponse> {
        if (!params) {
            params = {userId: this.authService.getUserIdFromToken()};
            if (this.selectedRoleOrgContext) {
                params.organizationId = this.selectedRoleOrgContext.organizationId;
                params.regionId = this.selectedRoleOrgContext.regionId;
                params.hubId = this.selectedRoleOrgContext.hubId;
            }
        }
        return this.request(
            '/user/getvehicles',
            params,
            component,
            USER_EVENT_TYPE.GET_VEHICLES
        );
    } // getVehicles()

    /**
     * get possible vehicle makes
     */
    getVehicleMakes(params: any, component: string): Observable<{id: number, name: string}[]> {
        return this.request(
            '/user/getvehiclemakes',
            params,
            component,
            USER_EVENT_TYPE.GET_VEHICLE_MAKES,
            true
        );
    } // getVehicleMakes()

    /**
     * get possible vehicle models based on the year and make
     */
    getVehicleModels(params: any) {
        return this.http.post(this.authService.getBackendUrl("/user/getvehiclemodels"), JSON.stringify(params), this.authService.getBasicAuthHeaders())
            .catch(error => {
                return this.handleError(error);
            });
    } // getVehicleModels()

    /**
     * get the possible engines that go with the vehicle
     */
    getVehicleEngines(params: any) {
        return this.http.post(this.authService.getBackendUrl("/user/getvehicleengines"), JSON.stringify(params), this.authService.getBasicAuthHeaders())
            .catch(error => {
                return this.handleError(error);
            });
    } // getVehicleEngines()

    /**
     * creates a new vehicle
     * @param params
     * @param component
     */
    newVehicle(params: any, component: string) {
        return this.request(
            '/user/newvehicle',
            params,
            component,
            USER_EVENT_TYPE.NEW_VEHICLE,
            true
        );
    } // newVehicle()

    /**
     * adds a vehicle to a user
     * @param params
     */
    addVehicle(params: Vehicle, component: string): Observable<AddVehicleResponse> {
        return this.request(
            "/user/addvehicle",
            params,
            component,
            USER_EVENT_TYPE.ADD_VEHICLE
        );
    } // addVehicle()

    /**
     * adds multiple vehicles to a user
     * @param params
     */
    importVehicles(params: any) {
        return this.http.post(this.authService.getBackendUrl("/user/importvehicles"), JSON.stringify(params), this.authService.getAuthHeaders())
            .catch(error => {
                return this.handleError(error);
            });
    } // importVehicles()

    /**
     * removes a vehicle from a user
     * @param params
     */
    removeVehicle(params: any, component: string = 'user-service') {
        return this.request(
            '/user/removevehicle',
            params,
            component,
            USER_EVENT_TYPE.REMOVE_VEHICLE
        );
    } // removeVehicle()

    /**
     * update a vehicle
     * @param params
     */
    updateVehicle(params: any, component: string) {
        return this.request(
            '/user/updatevehicle',
            params,
            component,
            USER_EVENT_TYPE.UPDATE_VEHICLE
        );
    } // updateVehicle()

    /**
     * validates a location as in our service area
     * @param params
     */
    subscribe(params: any) {
        return this.http.post(this.authService.getBackendUrl("/user/subscribe"), JSON.stringify(params), this.authService.getBasicAuthHeaders())
            .catch(error => {
                return this.handleError(error);
            });
    } // subscribe()

    /**
     * send contact email
     * @param params
     */
    sendApplication(params: any) {
        return this.http.post(this.authService.getBackendUrl("/provider/providerapplication"), JSON.stringify(params), this.authService.getBasicAuthHeaders())
            .catch(error => {
                return this.handleError(error);
            });
    }

    /**
     * net promoter score
     * @param params
     */
    nps(params: any) {
        return this.http.post(this.authService.getBackendUrl("/user/usernps"), JSON.stringify(params), this.authService.getAuthHeaders())
            .catch(error => {
                return this.handleError(error);
            });
    } // nps()

    /**
     * send feedback
     * @param params
     */
    feedback(params: any) {
        return this.http.post(this.authService.getBackendUrl("/user/feedback"), JSON.stringify(params), this.authService.getAuthHeaders())
            .catch(error => {
                return this.handleError(error);
            });
    } // feedback()

    /**
     * save referral info
     * @param params
     */
    updateAdChannel(params: any, component: string) {
        return this.request(
            '/user/updateadchannel',
            params,
            component,
            USER_EVENT_TYPE.UPDATE_AD_CHANNEL
        );
    } // updateAdChannel()

    getProviderRatings(params: any) {
        return this.http.post(this.authService.getBackendUrl("/provider/getproviderratings"), JSON.stringify(params), this.authService.getBasicAuthHeaders())
            .catch(error => {
                return this.handleError(error);
            });
    } // getProviderRatings()

    unsubscribe(params: any) {
        return this.http.post(this.authService.getBackendUrl("/user/unsubscribe"), JSON.stringify(params), this.authService.getBasicAuthHeaders())
            .catch(error => {
                return this.handleError(error);
            });
    } // unsubscribe()

    disableUser(params: {userId: number, banished?: boolean}): Observable<ServiceResponse> {
        return this.http.post<ServiceResponse>(this.authService.getBackendUrl("/user/disableuser"), JSON.stringify(params), this.authService.getAuthHeaders())
            .catch(error => {
                return this.handleError(error);
            });
    } // disableUser()

    updateUserStatus(userId: number, statusCode: number, component: string) {
        return this.request(
            '/user/updateuserstatus',
            {userId: userId, statusCode: statusCode},
            component,
            USER_EVENT_TYPE.UPDATE_USER_STATUS
        );
    } // updateUserStatus()

    getProvider(params: any) {
        return this.http.post(this.authService.getBackendUrl("/provider/getprovider"), JSON.stringify(params), this.authService.getBasicAuthHeaders())
            .catch(error => {
                return this.handleError(error);
            });
    } // getProviderRatings()

    getCommunicationOptions(component: string) {
        return this.request(
            '/user/getcommunicationoptions',
            {},
            component,
            USER_EVENT_TYPE.GET_COMMUNICATION_OPTIONS
        );
    } // getCommunicationOptions()

    getCommunicationPreferences(userId: number, component: string) {
        return this.request(
            '/user/getcommunicationpreferences',
            {userId: userId},
            component,
            USER_EVENT_TYPE.GET_COMMUNICATION_PREFERENCES
        );
    } // getCommunicationPreferences()

    setCommunicationPreferences(params: any, component: string) {
        return this.request(
            '/user/setcommunicationpreferences',
            params,
            component,
            USER_EVENT_TYPE.UPDATE_COMMUNICATION_PREFERENCES
        );
    } // setCommunicationPreferences()

    getContactList() {
        return this.contactList.asObservable();
    } // getContactList()

    updateContactList(contactList: Contact[]) {
        this.contactList.next(contactList);
    } // updateContactList()

    getContacts() {
        let params: any = {userId: this.authService.getUserIdFromToken()};
        if (this.selectedRoleOrgContext) {
            params.organizationId = this.selectedRoleOrgContext.organizationId;
            params.regionId = this.selectedRoleOrgContext.regionId;
            params.hubId = this.selectedRoleOrgContext.hubId;
        }
        return this.http.post(this.authService.getBackendUrl("/user/getcontacts"), JSON.stringify(params), this.authService.getAuthHeaders())
            .map((resp: any) => {
                if (!this.utilitiesService.isEmpty(resp.responseCode) && resp.responseCode == "100" && !this.utilitiesService.isEmpty(resp.contacts)) {
                    let contactList = resp.contacts;

                    this.utilitiesService.setLocal("contactList", JSON.stringify(contactList));
                    this.updateContactList(contactList);
                    return contactList;
                } else {
                    return {};
                }
            })
            .catch(error => {
                return this.handleError(error);
            });
    } // getContacts()

    updateContact(params: any) {
        return this.http.post(this.authService.getBackendUrl("/user/updatecontact"), JSON.stringify(params), this.authService.getAuthHeaders())
            .catch(error => {
                return this.handleError(error);
            });
    } // updateContact()

    deleteContact(params: any) {
        return this.http.post(this.authService.getBackendUrl("/user/deletecontact"), JSON.stringify(params), this.authService.getAuthHeaders())
            .catch(error => {
                return this.handleError(error);
            });
    } // deleteContact()

    /**
     * gets landing page detail
     */
    getLandingPageDetail(params: any, component: string) {
        return this.request(
           '/user/getlandingpagedetail',
            params,
            component,
            USER_EVENT_TYPE.GET_LANDING_PAGE_DETAILS,
            true
        );
    } // getLandingPageDetail()

    fetchVehicle(params: FetchVehicleOptions, component: string = 'user-service'): Observable<any> {
        return this.request(
            '/user/fetchvehicle',
            params,
            component,
            USER_EVENT_TYPE.FETCH_VEHICLE
        );
    } // fetchVehicle()

    transferVehicle(params: TransferVehicleOptions, component: string): Observable<any> {
        return this.request(
            '/user/transfervehicles',
            params,
            component,
            USER_EVENT_TYPE.TRANSFER_VEHICLES
        );
    } // transferVehicle()

    applyPayment(params: ApplyPaymentOptions, component: string): Observable<any> {
        return this.request(
            '/transaction/applypayment',
            params,
            component,
            USER_EVENT_TYPE.APPLY_PAYMENT
        );
    } // applyPayment()

    lookupVehicles(params: LookupVehiclesParams, component: string): Observable<LookupVehiclesResponse> {
        return this.request(
            '/user/lookupvehicles',
            params,
            component,
            USER_EVENT_TYPE.LOOKUP_VEHICLES
        );
    } // lookupVehicles()

    getMarketData(params: any, component: string): Observable<any> {
        return this.request(
            '/job/getmarket',
            params,
            component,
            USER_EVENT_TYPE.GET_MARKET
        ).pipe(
            mergeMap((result: any) => {
                if (result.responseCode == 100) {
                    UserService._marketData.next(result.market);
                }
                return of(result);
            })
        );

    } // transferVehicle()

    addVehicles(params: AddVehiclesParams, component: string): Observable<any> {
        return this.request(
            '/user/addvehicles',
            params,
            component,
            USER_EVENT_TYPE.ADD_VEHICLES
        );
    } // transferVehicle()

    updateVehicleStatus(params: Vehicle, component: string): Observable<ServiceResponse> {
        return this.request(
            '/user/updatevehiclestatus',
            params,
            component,
            USER_EVENT_TYPE.UPDATE_VEHICLE_STATUS
        );
    } // updateVehicleStatus()

    getApplicationConstants(): Observable<any> {
        return this.request('/user/getapplicationconstants', {}, 'user-service', USER_EVENT_TYPE.GET_APPLICATION_CONSTANTS, true);
    } // getAccounts()

    // handle 308 (out of service area) error by the caller
    private passThroughNonSuccess(url: string, params: YmServiceableZipParams | LsServiceableLocationParams,
                        userEvent: ValueLabel, component: string) {
        return this.http.post(url, JSON.stringify(params),
            this.authService.getBasicAuthHeaders()).pipe(map(resp => {
            const r: ServiceResponse = resp as ServiceResponse;
            this.eventTrackingService.trackSuccessEvent(component, "success", userEvent, url, params, r.responseCode);
            return r;
        })).catch(error => {
            this.eventTrackingService.trackErrorEvent(component, "error", userEvent, url, params, 0);
            return this.handleError(error);
        });
    } // passThroughNonSuccess()

    ymServiceableZip(params: YmServiceableZipParams, component: string): Observable<ServiceResponse> {
        const url = this.authService.getBackendUrl("/user/ymserviceablezip");
        return this.passThroughNonSuccess(url, params, USER_EVENT_TYPE.CHECK_YM_ZIP, component);
    } // ymServiceableZip()

    lsServiceableLocation(params: LsServiceableLocationParams, component: string): Observable<ServiceResponse> {
        const url = this.authService.getBackendUrl("/user/lsserviceablelocation");
        return this.passThroughNonSuccess(url, params, USER_EVENT_TYPE.CHECK_LS_ZIP, component);
    } // lsServiceableLocation()

    // checks if the user is fleet, and if their account is 30 days young
    isNewFleetUser() {
        const userData = this.getCurrentUserData() || {};
        const thirtyDaysInMilliseconds = (1000 * 60 * 60 * 24 * 30);
        const timeSinceSignup = new Date().getTime() - new Date(userData.signupDate).getTime();
        return UtilitiesService.isNotEmpty(userData)
            && userData.fleet
            // if signupDate is undefined, the user has not been created
            && (timeSinceSignup <= thirtyDaysInMilliseconds || !userData.hasOwnProperty('signupDate'));
    } // isNewFleetUser()

    getUserRoles(): Role[] {
        let roles: Role[];
        const userData = this.getCurrentUserData() || {};
        const excludedRoles = ['master', 'minion'];
        if (userData && (userData.orgType === ORGANIZATION_TYPES.FLEET || userData.fleet || userData.orgType == null)) {
            excludedRoles.push('user');
        }

        if (userData && userData.roles && userData.roles.length) {
            roles =  userData.roles.filter((item: any) => !excludedRoles.includes(item.roleName));
        }
        return roles;
    } // getUserRoles()

    refreshUserRole(): void {
        this.getUser().subscribe((result) => {
            const roles = this.getUserRoles();
            const refreshedRole = roles.find((role) => role.id === this.roleService.currentRoleId);
            this.roleService.setUserRole(refreshedRole);
        });
    }

    isYMUser(email: string): Observable<any> {
        const params: string = 'email=' + encodeURIComponent(email);
        return this.http.get(
            this.authService.getBackendUrl("/ym/common/v1/existing_customer") + '?' + params)
            .pipe(map(resp => {
                return resp;
            }, (error: any) => {
                return throwError(error);
            }));
    }

    setRole(user: User) {
        const roles = this.getUserRoles();
        if (roles != undefined && roles.length > 0) {
            this.roleService.setUserRole(roles[0]);
        } else {
            this.utilitiesService.throwSnackBar("A role is required. Please have an administrator assign a role for this user", true);
        }
    } // setRole

    importEmployees(params: ImportEmployeesOptions, component: string): Observable<ImportEmployeesResponse> {
        return this.request(
            '/user/importemployees',
            params,
            component,
            USER_EVENT_TYPE.IMPORT_EMPLOYEES
        );
    } // importEmployees()

    findRoles(component: string): Observable<FindRolesResponse> {
        const params: FindRolesOptions = {
            userId: this.authService.getUserIdFromToken(),
            actorUserRoleId: this.roleService.selectedUserRole$.getValue().userRoleId
        };
        return this.request(
            '/user/findroles',
            params,
            component,
            USER_EVENT_TYPE.FIND_ROLES
        );
    } // findRoles()

    setAllowedRoles(component: string) {
        this.findRoles(component).subscribe((response: FindRolesResponse) => {
            if (response.roles.length > 0) {
                this.roleService.setAllowedRoles(response.roles);
            }
        });
    } // setAllowedRoles
}
